// src/components/Dashboard/Dashboard.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../../services/api';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Backdrop,
  Fade,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  CopyAll as CopyIcon, 
  Visibility, 
  VisibilityOff, 
  CheckCircle as CheckCircleIcon,
  Sync as SyncIcon,
  PersonOutline as PersonIcon,
  CardMembership as VipIcon,
  PeopleOutline as ReferralIcon,
  AccountBalanceWallet as WalletIcon,
  VerifiedUser as VerifiedIcon,
  TrendingUp as TrendingIcon,
  AdminPanelSettings as AdminIcon,
  Support as SupportIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { logoutUser } from '../../services/firebaseAuth';
import API from '../config';
import Tutorial from '../Tutorial';
import SubscriptionModal from '../Subscription/SubscriptionModal';
import ReviewModal from '../Review/ReviewModal';
import MFAModal from '../Auth/MFAModal';
import CredentialsModal from '../Auth/CredentialsModal';

export default function Dashboard() {
  const { authData, resetTutorial } = useContext(AuthContext);
  const token = authData?.access_token;
  const navigate = useNavigate();
  const theme = useTheme();

  // State
  const [profile, setProfile] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editProfile, setEditProfile] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);

  const [showSubscriptionList, setShowSubscriptionList] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewCreator, setReviewCreator] = useState(null);
  const [refreshingSubscriptions, setRefreshingSubscriptions] = useState(false);
  const [showRefreshCredentialsModal, setShowRefreshCredentialsModal] = useState(false);
  const [refreshCredentials, setRefreshCredentials] = useState({
    email: '',
    password: ''
  });
  const [showRefreshPassword, setShowRefreshPassword] = useState(false);
  const [refreshingWithCredentials, setRefreshingWithCredentials] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    recommend: 'yes',
    spent: '',
    contentQuality: 3,
    contentFrequency: 3,
    authenticity: 3,
    consistency: 3,
    engagement: 3,
    fullReview: '',
  });
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [triggeringMFA, setTriggeringMFA] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    if (token) {
      api
        .get(`${API}/api/admin/stats`)
        .then(() => {
          setIsAdmin(true);
        })
        .catch(() => {
          setIsAdmin(false);
        });
    } else {
      setIsAdmin(false);
    }
  }, [token]);

  // Redirect if not logged in
  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  // Function to fetch dashboard data - defined outside useEffect for reusability
  const fetchDashboardData = useCallback(() => {
    if (!token) return;
    const apiUrl = `${API}/api/dashboard`;
    api
      .get(apiUrl)
      .then(({ data }) => {
        setProfile(data.profile);
        setHasActiveSubscription(Boolean(data.hasActiveSubscription));
        setEditProfile(data.profile);
        
        // Set verification status for UI
        setVerificationStatus(data.profile.verificationStatus);
        
        // Show MFA modal only if code was actually sent (user triggered it)
        if (data.profile.mfaCodeSent) {
          setShowMFAModal(true);
        }
      })
      .catch((err) => {
        // Check if account is blocked
        if (err.response?.status === 403 && err.response?.data?.error?.toLowerCase().includes('blocked')) {
          const errorMessage = err.response.data.error;
          setError(errorMessage);
          toast.error(errorMessage, { autoClose: 10000 });
          // Log out the user after showing the error
          setTimeout(() => {
            logoutUser();
            navigate('/login');
          }, 3000);
        } else {
          setError('Failed to load dashboard data.');
        }
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
    
    // Check if returning from payment success and refresh data
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment_success') === 'true') {
      // Give backend time to process webhook/verification, then refresh
      setTimeout(() => {
        fetchDashboardData();
      }, 2000);
    }
  }, [fetchDashboardData]);
  
  // Handle MFA success
  const handleMFASuccess = () => {
    toast.success('MFA code submitted! Your subscriptions will be fetched shortly.');
    setShowMFAModal(false);  // Close modal immediately
    setVerificationStatus('mfa_code_submitted');
    
    // Start polling for completion
    const startTime = Date.now();
    const pollInterval = setInterval(async () => {
      try {
        // Check for timeout
        if (Date.now() - startTime > 360000) { // 6 minutes
          clearInterval(pollInterval);
          toast.error('Verification timed out. Please try again.', { autoClose: 8000 });
          fetchDashboardData();
          return;
        }

        const { data } = await api.get(
          `${API}/api/subscriptions/verification-status`
        );
        
        // Check for failed status
        if (data.verificationStatus === 'failed') {
          clearInterval(pollInterval);
          toast.error('Verification failed. Please try again.');
          fetchDashboardData();
          return;
        }
        
        if (data.verified && data.hasSubscriptions) {
          // Success! Refresh full dashboard
          fetchDashboardData();
          toast.success('✅ Verification complete! Subscriptions loaded.');
          clearInterval(pollInterval);
        }
      } catch (err) {
        clearInterval(pollInterval);
        toast.error('Error checking verification status');
      }
    }, 3000);
  };

  // Check for first-time login and show tutorial
  // Use database field (hasSeenTutorial) instead of localStorage
  useEffect(() => {
    // Show tutorial if user hasn't seen it yet (from database)
    // Check both authData.isFirstLogin (set from profile) and profile.hasSeenTutorial
    const shouldShowTutorial = (
      (authData?.isFirstLogin === true) || 
      (profile && profile.hasSeenTutorial === false)
    );
    
    if (shouldShowTutorial && !loading) {
      setShowTutorial(true);
    }
  }, [authData?.isFirstLogin, profile?.hasSeenTutorial, loading]);

  // Fetch referral stats from Rewardful
  useEffect(() => {
    if (!profile?.email || !token) return;
    
    // Get Rewardful referral link
    let rewardfulLink = '';
    if (window.rewardful) {
      try {
        // Rewardful automatically generates referral links
        // We can get it from the Rewardful object or construct it
        rewardfulLink = window.rewardful?.referral?.link || '';
      } catch (e) {
        console.error('Error getting Rewardful link:', e);
      }
    }
    
    // Fetch stats from backend (which will call Rewardful API)
    // Use token from authData directly (same pattern as other components)
    const apiUrl = `${API}/api/rewardful/stats`;
    
    // Use api service which has interceptor, but also ensure token is available
    api
      .get(apiUrl)
      .then(({ data }) => {
        setReferralStats({
          ...data,
          referralLink: rewardfulLink || data.referralLink || `${window.location.origin}?ref=${profile._id}`
        });
      })
      .catch((err) => {
        // Fallback to manual stats if Rewardful fails
        if (profile?.username) {
          const fallbackUrl = `${API}/api/creators/username/${profile.username}`;
          api
            .get(fallbackUrl)
            .then(({ data }) => {
              setReferralStats(data);
            })
            .catch(() => {});
        }
      });
  }, [profile, token]);

  const copyToClipboard = (txt, msg) =>
    navigator.clipboard.writeText(txt)
      .then(() => toast.success(msg))
      .catch(() => toast.error('Copy failed'));
  
  // Trigger MFA verification - show credentials modal first
  const handleTriggerMFA = () => {
    setShowCredentialsModal(true);
  };
  
  // Handle credentials submission from modal
  const handleCredentialsSubmit = async ({ email, password }) => {
    setTriggeringMFA(true);
    
    try {
      // Set status to show processing banner (not fullscreen)
      setVerificationStatus('processing');
      setShowCredentialsModal(false);  // Close modal
      
      await api.post(
        `${API}/api/subscriptions/trigger-mfa-verification`,
        { email, password }  // Send OnlyFans credentials
      );
      
      toast.success('Verification started! Check your OnlyFans for a code.');
      
      // Start polling for status
      const startTime = Date.now();
      const pollInterval = setInterval(async () => {
        try {
          // Check for timeout
          if (Date.now() - startTime > 360000) { // 6 minutes
            clearInterval(pollInterval);
            toast.error('Verification timed out. Please try again.', { autoClose: 8000 });
            fetchDashboardData();
            return;
          }

          const { data } = await api.get(
            `${API}/api/subscriptions/verification-status`
          );
          
          // Check for failed status
          if (data.verificationStatus === 'failed') {
            clearInterval(pollInterval);
            toast.error('Verification failed. Please try again.');
            fetchDashboardData();
            return;
          }
          
          if (data.mfaCodeSent) {
            setShowMFAModal(true);
            clearInterval(pollInterval);
          }
          
          if (data.verified) {
            // Success! Refresh dashboard
            fetchDashboardData();
            clearInterval(pollInterval);
          }
        } catch (err) {
          clearInterval(pollInterval);
          toast.error('Error checking verification status');
        }
      }, 2000);  // Check every 2 seconds
      
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start verification');
      setVerificationStatus(null);  // Reset status on error
    } finally {
      setTriggeringMFA(false);
    }
  };

  // Profile edit handlers
  const handleEditToggle = () => {
    if (editing && profile) setEditProfile(profile);
    setEditing(e => !e);
  };
  const handleEditChange = e => {
    const { name, value } = e.target;
    setEditProfile(p => ({ ...p, [name]: value }));
  };
  const handleFileChange = e => {
    const { files } = e.target;
    if (files[0]) setProfileImage(files[0]);
  };
  const handleEditSubmit = async e => {
    e.preventDefault();
    const fd = new FormData();
    ['username', 'bio', 'birthdate', 'ofLink'].forEach(k => fd.append(k, editProfile[k] || ''));
    if (profileImage) fd.append('profilePicture', profileImage);
    
    try {
      const { data } = await api.put(`${API}/api/dashboard/profile`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(data.profile);
      setProfileImage(null);
      setEditing(false);
      toast.success('Profile updated successfully!');
      // Refresh dashboard to get updated profile picture
      fetchDashboardData();
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.response?.data?.error || 'Failed to update profile.');
    }
  };

  // Review handlers
  const openReview = creator => {
    setReviewCreator(creator);
    setReviewForm({
      recommend: 'yes',
      spent: '',
      contentQuality: 3,
      contentFrequency: 3,
      authenticity: 3,
      consistency: 3,
      engagement: 3,
      fullReview: '',
    });
    setShowReview(true);
  };
  const handleReviewSubmit = async e => {
    e.preventDefault();
    if (!token || !reviewCreator) return;
    const payload = { creator_id: reviewCreator._id, ...reviewForm };
    try {
      await api.post(`${API}/api/reviews`, payload);
      setShowReview(false);
    } catch {
      // Silent failure for now
    }
  };

  // Refresh subscriptions handlers
  const handleRefreshSubscriptions = () => {
    if (!token) return;
    // Open the credentials modal instead of directly refreshing
    setShowRefreshCredentialsModal(true);
  };

  const handleRefreshCredentialsChange = e => {
    const { name, value } = e.target;
    setRefreshCredentials(prev => ({ ...prev, [name]: value }));
  };

  const toggleRefreshPasswordVisibility = () => {
    setShowRefreshPassword(prev => !prev);
  };

  const handleRefreshWithCredentials = async e => {
    e.preventDefault();
    if (!token) return;
    
    // Validate inputs
    if (!refreshCredentials.email || !refreshCredentials.password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    setRefreshingWithCredentials(true);
    try {
      await api.post(`${API}/api/subscriptions/refresh_subscriptions`, 
        refreshCredentials
      );
      toast.success('Subscription refresh started! Your subscriptions will be updated shortly.');
      // Reset form and close modal
      setRefreshCredentials({ email: '', password: '' });
      setShowRefreshCredentialsModal(false);
      
      // Poll for completion and refresh dashboard
      const startTime = Date.now();
      const pollInterval = setInterval(async () => {
        try {
          if (Date.now() - startTime > 180000) { // 3 minutes timeout
            clearInterval(pollInterval);
            return;
          }
          
          // Refresh dashboard data to check for updates
          await fetchDashboardData();
          
          // If we have updated subscriptions, stop polling
          // (This is a simple check - you can make it more sophisticated)
          clearInterval(pollInterval);
        } catch (err) {
          // Continue polling on error
        }
      }, 5000); // Poll every 5 seconds
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to refresh subscriptions.');
    } finally {
      setRefreshingWithCredentials(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'70vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'70vh', px:2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const origin = window.location.origin;
  // Use Rewardful referral link if available, otherwise fallback to manual link
  const referralLink = referralStats?.referralLink || (profile ? `${origin}?ref=${profile._id}` : '');
  const activeSubsCount = profile?.subscriptions?.length || 0;
  // Use lastScrapeAt for displaying last sync time (converts to user's local timezone automatically)
  let lastSyncDate = null;
  if (profile?.lastScrapeAt) {
    const parsedDate = new Date(profile.lastScrapeAt);
    // Check if valid date
    if (!isNaN(parsedDate.getTime())) {
      lastSyncDate = parsedDate;
    }
  }

  return (
    <>
      {/* Removed fullscreen loading - using banner notification instead */}

      <Box maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, px: 0, mx: 'auto' }}>
        <Box sx={{ px: { xs: 2, sm: 3, md: 3 } }}>
      {/* MFA Verification Alert - Shows when MFA detected but user hasn't triggered yet */}
      {profile && profile.mfaRequired && !profile.verified && !verificationStatus && (
        <Card 
          sx={{ 
            mb: 4, 
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
            border: 'none',
          }}
        >
          {/* Animated Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
              animation: 'slide 20s linear infinite',
              '@keyframes slide': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(40px)' },
              },
            }}
          />
          
          <CardContent sx={{ p: 4, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              {/* Animated Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  animation: 'glow 2s ease-in-out infinite',
                  '@keyframes glow': {
                    '0%, 100%': {
                      boxShadow: '0 8px 32px rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1)',
                    },
                    '50%': {
                      boxShadow: '0 8px 32px rgba(255, 255, 255, 0.4), 0 0 20px rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.05)',
                    },
                  },
                }}
              >
                <Typography variant="h2" sx={{ animation: 'spin 3s linear infinite' }}>
                  🔐
                </Typography>
              </Box>
              
              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h4" 
                  fontWeight={800} 
                  gutterBottom
                  sx={{ 
                    color: 'white',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                    mb: 1,
                  }}
                >
                  🎯 Verification Required
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    mb: 3,
                    lineHeight: 1.6,
                    fontSize: '1.05rem',
                  }}
                >
                  Your OnlyFans account has two-factor authentication enabled.
                  Ready to verify? We'll send a code to your OnlyFans account.
                </Typography>
                
                <Button
                  variant="contained"
                  onClick={handleTriggerMFA}
                  disabled={triggeringMFA}
                  size="large"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    color: '#764ba2',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {triggeringMFA ? (
                    <>
                      <CircularProgress size={20} sx={{ color: '#764ba2', mr: 1 }} />
                      Starting...
                    </>
                  ) : (
                    <>
                      ✨ Start Verification
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Profile Header Card */}
      <Fade in timeout={600}>
        <Card sx={{ 
          mb: 3, 
          borderRadius: 3, 
          overflow: 'hidden', 
          boxShadow: 3,
          animation: 'slideUp 0.6s ease-out',
          '@keyframes slideUp': {
            from: {
              opacity: 0,
              transform: 'translateY(20px)',
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
            p: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '40%',
              height: '100%',
              opacity: 0.1,
              background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
            }}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <Avatar
              src={profile.profilePicture}
              alt={profile.username}
              sx={{
                width: { xs: 80, sm: 100 },
                height: { xs: 80, sm: 100 },
                border: '4px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              }}
            />
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: 'white', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                  {profile.username}
                </Typography>
                {profile.verified && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ color: 'white !important', fontSize: '1rem' }} />}
                    label="OF VERIFIED"
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                      color: 'white',
                      fontWeight: 700,
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                    }}
                  />
                )}
                {profile.isCreator && (
                  <Chip
                    label="✨ CREATOR"
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 12px rgba(240, 147, 251, 0.4)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      animation: 'shimmer 3s ease-in-out infinite',
                      '@keyframes shimmer': {
                        '0%, 100%': {
                          filter: 'brightness(1)',
                        },
                        '50%': {
                          filter: 'brightness(1.2)',
                        },
                      },
                      '& .MuiChip-label': {
                        px: 1.5,
                        textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                        letterSpacing: '0.5px',
                      },
                    }}
                  />
                )}
              </Box>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}>
                {profile.bio || 'No bio yet'}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<PersonIcon />}
                onClick={handleEditToggle}
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                Edit Profile
              </Button>
                {isAdmin && (
                  <Button
                    component={RouterLink}
                    to="/admin/reports"
                    variant="contained"
                    startIcon={<AdminIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #ee5a6f 0%, #ff6b6b 100%)',
                        boxShadow: '0 6px 16px rgba(255, 107, 107, 0.5)',
                      },
                    }}
                  >
                    Admin Dashboard
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
        </Card>
      </Fade>

      {/* Edit Profile Modal/Drawer */}
      <Dialog open={editing} onClose={handleEditToggle} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleEditSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
          >
            <TextField
              label="Username"
              name="username"
              value={editProfile.username}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              label="Bio"
              name="bio"
              value={editProfile.bio}
              onChange={handleEditChange}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Birthdate"
              name="birthdate"
              value={editProfile.birthdate}
              onChange={handleEditChange}
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Button component="label" variant="outlined" fullWidth>
              {profileImage ? `Selected: ${profileImage.name}` : 'Upload Avatar'}
              <input hidden type="file" accept="image/*" onChange={handleFileChange} />
            </Button>
            <TextField
              label="OnlyFans Link"
              name="ofLink"
              value={editProfile.ofLink}
              onChange={handleEditChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditToggle}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

        </Box>

      {/* Main Dashboard Grid */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 3 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 2.5, md: 3 },
            alignItems: 'stretch',
          }}
        >
        {/* VIP/Account Status Card */}
        <Fade in timeout={800}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: 2, 
            display: 'flex', 
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            animation: 'slideUp 0.7s ease-out 0.2s backwards',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            },
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VipIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Account Status
                  </Typography>
                </Box>
              </Box>

              {hasActiveSubscription ? (
                // Has VIP subscription
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
                      position: 'relative',
                      overflow: 'visible',
                      '&::after': {
                        content: '"✨"',
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        fontSize: '1.2rem',
                        animation: 'sparkle 2s ease-in-out infinite',
                      },
                      '@keyframes sparkle': {
                        '0%, 100%': {
                          opacity: 0.4,
                          transform: 'scale(0.8) rotate(0deg)',
                        },
                        '50%': {
                          opacity: 1,
                          transform: 'scale(1.2) rotate(180deg)',
                        },
                      },
                    }}
                  >
                    <Typography variant="h3">👑</Typography>
                  </Box>
                  <Chip
                    label="VIP MEMBER"
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      mb: 2,
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.35)',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You're a VIP member with full access
                  </Typography>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to="/subscription"
                    fullWidth
                  >
                    Manage Subscription
                  </Button>
                </Box>
              ) : (
                // No VIP subscription
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 20px rgba(245, 87, 108, 0.3)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        opacity: 0.3,
                        animation: 'pulse 2s ease-in-out infinite',
                      },
                      '@keyframes pulse': {
                        '0%, 100%': {
                          transform: 'scale(1)',
                        },
                        '50%': {
                          transform: 'scale(1.1)',
                        },
                      },
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 40, color: 'white', position: 'relative', zIndex: 1 }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Free Account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Upgrade to VIP for exclusive features and benefits
                  </Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/subscription"
                    fullWidth
                    sx={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                      },
                    }}
                  >
                    Upgrade to VIP
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>

        {/* Referral Dashboard Card */}
        <Fade in timeout={1000}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: 2, 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            animation: 'slideUp 0.7s ease-out 0.3s backwards',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            },
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ReferralIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Referral Dashboard
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Invite friends and earn rewards
                  </Typography>
                </Box>
              </Box>

              {/* Referral Explanation */}
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  mb: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%)',
                  border: '1px solid',
                  borderColor: 'rgba(79, 172, 254, 0.25)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                  },
                }}
              >
                <Typography 
                  variant="body1" 
                  fontWeight={700} 
                  sx={{ 
                    mb: 1.5,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  }}
                >
                  Earn $1 for every friend who upgrades to VIP!
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    mb: 1.5,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  }}
                >
                  Share your referral link and get paid when they subscribe.
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    background: 'rgba(79, 172, 254, 0.15)',
                    border: '1px solid',
                    borderColor: 'rgba(79, 172, 254, 0.3)',
                    mb: 1.5,
                  }}
                >
                  <Typography 
                    variant="caption" 
                    fontWeight={700}
                    sx={{ 
                      color: 'primary.main',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                    }}
                  >
                    Limited Time:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  >
                    Full $1 from first month
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  }}
                >
                  The more friends you refer, the more you earn—it's that simple.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                  gap: { xs: 2, sm: 2.5 },
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #4facfe20 0%, #00f2fe20 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 110,
                  }}
                >
                  <TrendingIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700} color="info.main">
                    {referralStats?.totalVisitors || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Total Visitors
                  </Typography>
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #1e88e520 0%, #1565c020 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 110,
                  }}
                >
                  <ReferralIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700} color="primary.main">
                    {referralStats?.totalReferrals || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Total Referrals
                  </Typography>
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #4caf5020 0%, #45a04920 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 110,
                  }}
                >
                  <WalletIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    ${(referralStats?.earnings ?? 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Earnings
                  </Typography>
                </Box>
              </Box>

              {/* Payout Information */}
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.warning.main, 0.3),
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                  💰 <strong>Payout Information:</strong> Referral payments are paid out after a 60-day waiting period to ensure conversions are valid.
                </Typography>
              </Box>

              {/* Dashboard Access Info */}
              {referralStats?.isConfirmed === false && (
                <Box
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.info.main, 0.3),
                  }}
                >
                  <Typography variant="body2" color="info.dark" fontWeight={600} gutterBottom>
                    📧 Check Your Email for Dashboard Access
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                    Rewardful will send you an invitation email to set up your affiliate dashboard account. 
                    Use the unique signup link in that email to create your account.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 1, fontStyle: 'italic' }}>
                    Note: The generic signup link in Rewardful's campaign settings may not work for automatically created affiliates.
                  </Typography>
                </Box>
              )}

              {referralStats?.dashboardUrl && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    href={referralStats.dashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: 600,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        bgcolor: 'primary.light',
                      },
                    }}
                  >
                    View Full Dashboard
                  </Button>
                </Box>
              )}

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={600}>
                  Your Referral Link
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    value={referralLink}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: 'rgba(0, 0, 0, 0.02)',
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => copyToClipboard(referralLink, 'Referral link copied!')}
                    sx={{
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
                      },
                    }}
                  >
                    <CopyIcon />
                  </IconButton>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* OnlyFans Subscriptions Card - Full Width */}
        <Fade in timeout={1200}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: 2, 
            gridColumn: { md: '1 / -1' },
            transition: 'all 0.3s ease',
            animation: 'slideUp 0.7s ease-out 0.4s backwards',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            },
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <VerifiedIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      OnlyFans Subscriptions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profile.verified ? 'Your synced OF subscriptions' : 'Connect your OF account'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {!profile.verified ? (
                // Not verified state
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    You don't have your OF profile connected. Sync your subscriptions to leave verified reviews.
                  </Typography>
                  <Box
                    sx={{
                      maxWidth: 520,
                      mx: 'auto',
                      mt: 2,
                      mb: 3,
                      px: { xs: 2.5, sm: 3 },
                      py: { xs: 1.75, sm: 2 },
                      borderRadius: 3,
                      background: 'linear-gradient(120deg, rgba(30,136,229,0.1), rgba(21,101,192,0.12))',
                      border: '1px solid rgba(30,136,229,0.2)',
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                      textAlign: 'left',
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 6px 16px rgba(30,136,229,0.18)',
                      }}
                    >
                      <VerifiedIcon sx={{ color: '#1e88e5' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Email login required
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Use your OnlyFans email + password (Google/X logins aren’t supported yet).
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SyncIcon />}
                    onClick={handleTriggerMFA}
                    sx={{
                      background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
                      },
                    }}
                  >
                    Verify with OnlyFans
                  </Button>
                </Box>
              ) : (
                // Verified state
                <>
                  <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 150 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Subscriptions
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="primary">
                        {activeSubsCount}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 150 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Sync
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {lastSyncDate ? lastSyncDate.toLocaleDateString() : 'Never'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lastSyncDate ? lastSyncDate.toLocaleTimeString() : ''}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<SyncIcon />}
                      onClick={handleRefreshSubscriptions}
                      disabled={refreshingSubscriptions}
                    >
                      {refreshingSubscriptions ? 'Syncing...' : 'Sync Now'}
                    </Button>
                    <Button variant="text" onClick={() => setShowSubscriptionList(true)}>
                      View All Subscriptions
                    </Button>
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Fade>

        {/* Support Button - Full Width */}
        <Fade in timeout={1400}>
          <Box sx={{ gridColumn: { md: '1 / -1' }, mt: 2 }}>
            <Button
              component={RouterLink}
              to="/support"
              variant="contained"
              fullWidth
              startIcon={<SupportIcon />}
              sx={{
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Contact Support
            </Button>
          </Box>
        </Fade>
        </Box>
      </Box>

      {/* Review Modal */}
      <ReviewModal
        open={showReview}
        onClose={() => setShowReview(false)}
        reviewCreator={reviewCreator}
        reviewForm={reviewForm}
        setReviewForm={setReviewForm}
        onSubmit={handleReviewSubmit}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        open={showSubscriptionList}
        onClose={() => setShowSubscriptionList(false)}
        subscriptions={profile?.subscriptions}
        onOpenReview={openReview}
        onRefreshSubscriptions={handleRefreshSubscriptions}
        refreshingSubscriptions={refreshingSubscriptions}
      />

      {/* Refresh Credentials Modal */}
      <Dialog
        open={showRefreshCredentialsModal}
        onClose={() => setShowRefreshCredentialsModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Refresh Subscriptions</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Must use the same credentials as your OnlyFans account. We encrypt your information—
            we never access or store your OF password.
          </Typography>
          
          <Box component="form" onSubmit={handleRefreshWithCredentials} sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={refreshCredentials.email}
                onChange={handleRefreshCredentialsChange}
                required
                fullWidth
              />
              
              <TextField
                label="Password"
                name="password"
                type={showRefreshPassword ? 'text' : 'password'}
                value={refreshCredentials.password}
                onChange={handleRefreshCredentialsChange}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={toggleRefreshPasswordVisibility}
                      edge="end"
                    >
                      {showRefreshPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRefreshCredentialsModal(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRefreshWithCredentials}
            disabled={refreshingWithCredentials}
          >
            {refreshingWithCredentials ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Tutorial Component */}
      <Tutorial 
        open={showTutorial} 
        onClose={() => {
          setShowTutorial(false);
          // Refresh profile to get updated hasSeenTutorial from database
          if (token) {
            fetchDashboardData();
          }
        }} 
      />
      
      {/* Credentials Modal */}
      <CredentialsModal
        open={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
        onSubmit={handleCredentialsSubmit}
        loading={triggeringMFA}
      />
      
      {/* MFA Modal */}
      <MFAModal
        open={showMFAModal}
        onClose={() => setShowMFAModal(false)}
        onSuccess={handleMFASuccess}
        token={token}
      />
    </Box>
    </>
  );
}
